import { Link } from '@tanstack/react-router';

import { LockedCard, UnlockedFeatureCard } from '@/components/feature/LockedCard';
import { Button } from '@/components/ui/button';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useLatestAudit } from '@/lib/hooks/useAudits';
import { usePermissions } from '@/lib/hooks/usePermissions';
import { hasFeature } from '@/lib/permissions';
import { cn } from '@/lib/utils';

export function DashboardPage() {
  const { data: perms } = usePermissions();
  const { data: latest } = useLatestAudit();
  const tier = perms?.tier ?? 'free';

  const score = latest?.overall_score ?? null;
  const scoreColor =
    score == null ? 'text-slate-300'
    : score <= 40 ? 'text-[var(--color-critical)]'
    : score <= 70 ? 'text-[var(--color-warning)]'
    : 'text-[var(--color-success)]';

  return (
    <div className="mx-auto max-w-6xl px-8 py-10">
      <header className="flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Dashboard</h1>
          <p className="mt-1 text-sm text-slate-500">
            You're on the <strong className="uppercase tracking-wide">{tier}</strong> plan.
          </p>
        </div>
        <Button asChild>
          <Link to="/app/audits">Audits</Link>
        </Button>
      </header>

      <div className="mt-8 grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle>Latest audit score</CardTitle>
            <CardDescription>
              {latest ? latest.score_category ?? `Audit ${latest.status}` : '0–100 across 10 dimensions.'}
            </CardDescription>
          </CardHeader>
          <div className={cn('text-4xl font-semibold', scoreColor)}>
            {score != null ? score : '—'}
            {score != null && <span className="ml-1 text-lg text-slate-400">/100</span>}
          </div>
          <p className="mt-2 text-xs text-slate-500">
            {latest
              ? <Link to="/app/audits/$auditId" params={{ auditId: latest.id }} className="underline">View report</Link>
              : <Link to="/app/specs" className="underline">Add a spec</Link>}
          </p>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Open PR checks</CardTitle>
            <CardDescription>Every PR touching your spec, SDK, or docs.</CardDescription>
          </CardHeader>
          <div className="text-4xl font-semibold text-slate-300">—</div>
          <p className="mt-2 text-xs text-slate-500">Install the GitHub App to start monitoring.</p>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Drift events, this month</CardTitle>
            <CardDescription>Dimension-score changes that crossed your thresholds.</CardDescription>
          </CardHeader>
          <div className="text-4xl font-semibold text-slate-300">—</div>
          <p className="mt-2 text-xs text-slate-500">Nothing yet.</p>
        </Card>
      </div>

      <h2 className="mt-10 mb-4 text-lg font-semibold">Features</h2>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <FeatureCard
          feature="asset.llms_txt"
          tier="starter"
          perms={perms}
          title="Generated llms.txt"
          description="llms.txt + llms-full.txt auto-generated and hosted on your subdomain."
        />
        <FeatureCard
          feature="pr_bot"
          tier="growth"
          perms={perms}
          title="PR-comment bot"
          description="Athena reviews every PR that touches your spec, SDK, or docs."
        />
        <FeatureCard
          feature="asset.mcp_server"
          tier="growth"
          perms={perms}
          title="MCP server"
          description="A working Model Context Protocol server for your API, hosted for you."
        />
        <FeatureCard
          feature="asset.cursor_rules"
          tier="growth"
          perms={perms}
          title="Cursor rules"
          description=".cursorrules file generated from your spec + docs."
        />
        <FeatureCard
          feature="weekly_drift"
          tier="growth"
          perms={perms}
          title="Weekly drift report"
          description="Monday mornings: what changed, what broke, what agents will hit."
        />
        <FeatureCard
          feature="api_access"
          tier="scale"
          perms={perms}
          title="Public API"
          description="/api/v1 access with per-token rate limits and webhooks."
        />
      </div>

      <Card className="mt-10">
        <CardHeader>
          <CardTitle>Next steps</CardTitle>
          <CardDescription>
            Get your first audit on the board, then connect GitHub so Athena watches every PR.
          </CardDescription>
        </CardHeader>
        <ol className="mt-2 space-y-2 text-sm text-slate-700 dark:text-slate-300">
          <li>1. <Link to="/app/specs" className="underline">Add your OpenAPI spec</Link>.</li>
          <li>2. <Link to="/app/repos" className="underline">Connect your SDK repos</Link>.</li>
          <li>3. <Link to="/app/settings/integrations" className="underline">Install the GitHub App</Link> to monitor every PR.</li>
        </ol>
      </Card>
    </div>
  );
}

function FeatureCard({
  feature,
  tier,
  perms,
  title,
  description,
}: {
  feature: string;
  tier: 'starter' | 'growth' | 'scale';
  perms: ReturnType<typeof usePermissions>['data'];
  title: string;
  description: string;
}) {
  if (!perms) {
    return (
      <Card className="animate-pulse">
        <CardHeader>
          <CardTitle className="opacity-40">{title}</CardTitle>
          <CardDescription>&nbsp;</CardDescription>
        </CardHeader>
      </Card>
    );
  }
  if (hasFeature(perms, feature)) {
    return <UnlockedFeatureCard title={title} description={description} />;
  }
  return <LockedCard title={title} description={description} requiredTier={tier} />;
}
